import {
  DefaultValues,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import {
  DistributedMaterial,
  Employee,
  EmployeeService,
  Material,
  WorkDay,
  WorkDayMaterialRequirement,
} from "../../api/generated";
import { useEffect } from "react";
import { showError } from "../../lib/toast";

export type DistributionMode = "issue" | "return";

interface DistributionFormProps {
  employee: Employee;
  workSchedule: WorkDay;
  distributedMaterials: DistributedMaterial[];
  mode: DistributionMode;
  onSave: () => void;
}

interface DistributedMaterialForm {
  selectableMaterials: ScheduledMaterial[];
  distributedMaterials: DistributedMaterial[];
}

interface ScheduledMaterial {
  material: Material;
  quantity: number;
}

function defaultValues(
  mode: DistributionMode,
  requiredMaterials: WorkDayMaterialRequirement[],
  distributedMaterials: DistributedMaterial[],
): DefaultValues<DistributedMaterialForm> {
  return {
    selectableMaterials: getSelectableMaterials(
      mode,
      requiredMaterials,
      distributedMaterials,
    ),
    distributedMaterials,
  };
}

function buildNetDistributedPerMaterial(
  distributedMaterials: DistributedMaterial[],
): Map<number, ScheduledMaterial> {
  const netPerMaterial = new Map<number, ScheduledMaterial>();

  distributedMaterials.forEach((distributedMaterial) => {
    const materialId = distributedMaterial.material.id;
    if (materialId === undefined) {
      return;
    }

    const current = netPerMaterial.get(materialId) ?? {
      material: distributedMaterial.material,
      quantity: 0,
    };

    current.quantity +=
      distributedMaterial.status === "distributed"
        ? distributedMaterial.quantity
        : -distributedMaterial.quantity;

    netPerMaterial.set(materialId, current);
  });

  return netPerMaterial;
}

function getIssueSelectableMaterials(
  requiredMaterials: WorkDayMaterialRequirement[],
  distributedMaterials: DistributedMaterial[],
): ScheduledMaterial[] {
  const netPerMaterial = buildNetDistributedPerMaterial(distributedMaterials);

  return requiredMaterials
    .map((requirement) => {
      const materialId = requirement.material.id;
      const alreadyDistributed =
        materialId === undefined
          ? 0
          : Math.max(netPerMaterial.get(materialId)?.quantity ?? 0, 0);

      return {
        material: requirement.material,
        quantity: requirement.requiredQuantity - alreadyDistributed,
      };
    })
    .filter((material) => material.quantity > 0);
}

function getReturnSelectableMaterials(
  distributedMaterials: DistributedMaterial[],
): ScheduledMaterial[] {
  const netPerMaterial = buildNetDistributedPerMaterial(distributedMaterials);

  return Array.from(netPerMaterial.values()).filter(
    (material) => material.quantity > 0,
  );
}

function getSelectableMaterials(
  mode: DistributionMode,
  requiredMaterials: WorkDayMaterialRequirement[],
  distributedMaterials: DistributedMaterial[],
): ScheduledMaterial[] {
  if (mode === "return") {
    return getReturnSelectableMaterials(distributedMaterials);
  }

  return getIssueSelectableMaterials(requiredMaterials, distributedMaterials);
}

const labels = {
  issue: {
    sourceTitle: "Gepland materiaal",
    sourceEmpty: "Er is geen gepland materiaal of alles is uitgegeven",
    sourceAction: "Uitgeven",
    resultTitle: "Uitgegeven materiaal",
    resultEmpty: "Er is (nog) geen materiaal uitgegeven",
  },
  return: {
    sourceTitle: "Uitgegeven materiaal",
    sourceEmpty: "Er is geen materiaal om te retourneren",
    sourceAction: "Retour nemen",
    resultTitle: "Te retourneren materiaal",
    resultEmpty: "Er is (nog) geen materiaal geselecteerd voor retour",
  },
} as const;

export const DistributionForm = (props: DistributionFormProps) => {
  const texts = labels[props.mode];
  const actionStatus: DistributedMaterial["status"] =
    props.mode === "issue" ? "distributed" : "returned";

  const { register, control, handleSubmit, getValues } =
    useForm<DistributedMaterialForm>({
      defaultValues: defaultValues(
        props.mode,
        props.workSchedule.materialRequirements,
        props.distributedMaterials,
      ),
    });

  const { fields: selectableMaterials, replace: replaceSelectableMaterials } =
    useFieldArray<DistributedMaterialForm, "selectableMaterials", "fieldId">({
      control,
      name: "selectableMaterials",
      keyName: "fieldId",
    });

  const { fields, append, remove } = useFieldArray<
    DistributedMaterialForm,
    "distributedMaterials",
    "fieldId"
  >({
    control,
    name: "distributedMaterials",
    keyName: "fieldId",
  });

  useEffect(() => {
    const nextSelectableMaterials = getSelectableMaterials(
      props.mode,
      props.workSchedule.materialRequirements,
      fields,
    );
    replaceSelectableMaterials(nextSelectableMaterials);
  }, [
    fields,
    props.mode,
    props.workSchedule.materialRequirements,
    replaceSelectableMaterials,
  ]);

  function addActionMaterial(
    materialId: number | undefined,
    index: number,
    event: React.MouseEvent<HTMLButtonElement>,
  ) {
    event.preventDefault();

    if (materialId === undefined) {
      return;
    }

    const selectableMaterial = selectableMaterials.find(
      (material) => material.material.id === materialId,
    );

    if (!selectableMaterial) {
      return;
    }

    append({
      material: selectableMaterial.material,
      quantity: getValues(`selectableMaterials.${index}.quantity`),
      status: actionStatus,
    });
  }

  const actionMaterials = fields
    .map((material, index) => ({ material, index }))
    .filter((item) => item.material.status === actionStatus);

  const onSubmit: SubmitHandler<DistributedMaterialForm> = async (data) => {
    try {
      await EmployeeService.postEmployeeScheduleDistributed(
        props.employee.id!,
        props.workSchedule.id!,
        {
          distributedMaterials: data.distributedMaterials,
        },
      );

      props.onSave();
    } catch (error) {
      console.log("Error saving distributed materials", error);
      showError(
        "Er is een fout opgetreden bij het opslaan van de materiaaluitgifte/retour. Probeer het opnieuw.",
      );
    }
  };

  const onError: SubmitErrorHandler<DistributedMaterialForm> = (errors) =>
    console.log(errors);

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="w-full">
      <h2 className="text-lg font-semibold">{texts.sourceTitle}</h2>
      <div className="w-full overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Materiaal</th>
              <th>Hoeveelheid</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {selectableMaterials.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center">
                  {texts.sourceEmpty}
                </td>
              </tr>
            )}
            {selectableMaterials.length > 0 &&
              selectableMaterials.map((material, index) => (
                <tr key={material.fieldId}>
                  <td>{material.material.name}</td>
                  <td>
                    <input
                      type="number"
                      min={1}
                      className="input"
                      defaultValue={material.quantity}
                      {...register(`selectableMaterials.${index}.quantity`, {
                        valueAsNumber: true,
                      })}
                    />
                  </td>
                  <td className="flex justify-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-accent"
                      onClick={(event) =>
                        addActionMaterial(material.material.id, index, event)
                      }
                    >
                      {texts.sourceAction}
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <h2 className="text-lg font-semibold">{texts.resultTitle}</h2>

      <div className="w-full overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Materiaal</th>
              <th>Hoeveelheid</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {actionMaterials.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center">
                  {texts.resultEmpty}
                </td>
              </tr>
            )}
            {actionMaterials.length > 0 &&
              actionMaterials.map(({ material, index }) => (
                <tr key={material.fieldId}>
                  <td>{material.material.name}</td>
                  <td>
                    <input
                      type="number"
                      className="input"
                      disabled
                      defaultValue={material.quantity}
                      {...register(`distributedMaterials.${index}.quantity`, {
                        required: true,
                        valueAsNumber: true,
                      })}
                    />
                  </td>
                  <td className="flex justify-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-error"
                      onClick={() => remove(index)}
                    >
                      Verwijder
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="w-full flex justify-end mt-4 mr-8">
        <button type="submit" className="btn btn-primary">
          Opslaan
        </button>
      </div>
    </form>
  );
};
